import React, { useState, useMemo } from 'react';
import {
  Search, ChevronDown, ChevronRight, Calculator, Database,
  Clock, Info, BookOpen,
} from 'lucide-react';
import {
  KPI_GLOSSARY, KPI_CATEGORIES, DOMAIN_LABELS, LEVEL_LABELS,
  type KPIEntry, type KPIDomain, type KPILevel, type KPICategory,
} from '../data/kpi-glossary';
import { DOMAIN_COLORS } from '../data/schema';

type DomainFilter = 'all' | KPIDomain;

const DOMAINS: DomainFilter[] = ['all', 'casino', 'sport', 'finance', 'player', 'cross'];

const LEVEL_ICONS: Record<KPILevel, React.ElementType> = {
  stored: Database,
  computed: Calculator,
  long_term: Clock,
};

const LEVEL_COLORS: Record<KPILevel, string> = {
  stored: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  computed: 'bg-amber-50 text-amber-700 border-amber-200',
  long_term: 'bg-purple-50 text-purple-700 border-purple-200',
};

const LEVEL_ICON_COLORS: Record<KPILevel, string> = {
  stored: 'text-emerald-600',
  computed: 'text-amber-600',
  long_term: 'text-purple-600',
};

function DomainBadge({ domain }: { domain: string }) {
  const colors = DOMAIN_COLORS[domain] || { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
      {DOMAIN_LABELS[domain as KPIDomain] || domain}
    </span>
  );
}

function LevelBadge({ level }: { level: KPILevel }) {
  const Icon = LEVEL_ICONS[level];
  const info = LEVEL_LABELS[level];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border ${LEVEL_COLORS[level]}`}>
      <Icon className="h-3 w-3" />
      {info.label}
    </span>
  );
}

function KPICard({ kpi, isExpanded, onToggle }: {
  kpi: KPIEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          {isExpanded
            ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
          }
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {kpi.id && (
                <span className="text-xs font-mono text-accent">{kpi.id}</span>
              )}
              <span className="font-medium text-slate-900">{kpi.name}</span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500 truncate">{kpi.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <DomainBadge domain={kpi.domain} />
          <LevelBadge level={kpi.level} />
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-4 space-y-3">
          <p className="text-sm text-slate-700">{kpi.description}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <DetailRow label="Metric Name" value={kpi.metric} mono />
              {kpi.formula && <DetailRow label="Formula" value={kpi.formula} mono />}
              <DetailRow label="Source Table" value={kpi.sourceTable} mono />
            </div>
            <div className="space-y-2">
              {kpi.variants && <DetailRow label="Variants" value={kpi.variants} />}
              {kpi.currencies && <DetailRow label="Currencies" value={kpi.currencies} />}
              {kpi.dwhMapping && <DetailRow label="DWH Mapping" value={kpi.dwhMapping} />}
            </div>
          </div>

          {kpi.notes && (
            <div className="flex items-start gap-2 rounded-lg bg-accent/5 border border-accent/20 p-3 mt-2">
              <Info className="h-4 w-4 text-accent shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600">{kpi.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <span className="text-xs font-medium text-slate-400">{label}</span>
      <p className={`text-sm text-slate-700 ${mono ? 'font-mono text-xs mt-0.5' : ''}`}>{value}</p>
    </div>
  );
}

export const KPIGlossary: React.FC = () => {
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');
  const [levelFilter, setLevelFilter] = useState<KPILevel | 'all'>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return KPI_GLOSSARY.filter((kpi) => {
      if (domainFilter !== 'all' && kpi.domain !== domainFilter) return false;
      if (levelFilter !== 'all' && kpi.level !== levelFilter) return false;
      if (q) {
        return (
          kpi.name.toLowerCase().includes(q) ||
          kpi.metric.toLowerCase().includes(q) ||
          kpi.id.toLowerCase().includes(q) ||
          kpi.description.toLowerCase().includes(q) ||
          kpi.category.toLowerCase().includes(q) ||
          (kpi.formula && kpi.formula.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [search, domainFilter, levelFilter]);

  const grouped = useMemo(() => {
    const map = new Map<KPICategory, KPIEntry[]>();
    for (const cat of KPI_CATEGORIES) {
      const items = filtered.filter((k) => k.category === cat);
      if (items.length > 0) map.set(cat, items);
    }
    return map;
  }, [filtered]);

  const toggle = (metric: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(metric)) next.delete(metric);
      else next.add(metric);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedIds(new Set(filtered.map((k) => k.metric)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  const counts = useMemo(() => ({
    stored: KPI_GLOSSARY.filter((k) => k.level === 'stored').length,
    computed: KPI_GLOSSARY.filter((k) => k.level === 'computed').length,
    long_term: KPI_GLOSSARY.filter((k) => k.level === 'long_term').length,
  }), []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">KPI Glossary</h1>
        <p className="mt-1 text-sm text-slate-500">
          Complete reference of all Key Performance Indicators tracked by bs-reporting
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Total KPIs"
          value={KPI_GLOSSARY.length}
          icon={BookOpen}
          color="text-accent"
        />
        <SummaryCard
          label="Stored (Gold)"
          value={counts.stored}
          icon={Database}
          color="text-emerald-600"
        />
        <SummaryCard
          label="Computed (API)"
          value={counts.computed}
          icon={Calculator}
          color="text-amber-600"
        />
        <SummaryCard
          label="Long-term"
          value={counts.long_term}
          icon={Clock}
          color="text-purple-600"
        />
      </div>

      {/* Level descriptions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(Object.entries(LEVEL_LABELS) as [KPILevel, { label: string; description: string }][]).map(
          ([level, info]) => {
            const Icon = LEVEL_ICONS[level];
            return (
              <div key={level} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`h-4 w-4 ${LEVEL_ICON_COLORS[level]}`} />
                  <span className="text-sm font-medium text-slate-900">{info.label}</span>
                </div>
                <p className="text-xs text-slate-500">{info.description}</p>
              </div>
            );
          }
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search KPIs by name, metric, formula..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Domain:</span>
          <div className="flex gap-1">
            {DOMAINS.map((d) => (
              <button
                key={d}
                onClick={() => setDomainFilter(d)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  domainFilter === d
                    ? 'bg-accent text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {d === 'all' ? 'All' : DOMAIN_LABELS[d]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Level:</span>
          <div className="flex gap-1">
            {(['all', 'stored', 'computed', 'long_term'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLevelFilter(l)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  levelFilter === l
                    ? 'bg-accent text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {l === 'all' ? 'All' : LEVEL_LABELS[l].label.split(' ')[0]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Expand/Collapse + count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {filtered.length} KPI{filtered.length !== 1 ? 's' : ''} found
        </p>
        <div className="flex gap-2">
          <button onClick={expandAll} className="text-xs text-accent hover:underline">
            Expand all
          </button>
          <span className="text-slate-300">|</span>
          <button onClick={collapseAll} className="text-xs text-accent hover:underline">
            Collapse all
          </button>
        </div>
      </div>

      {/* KPI list grouped by category */}
      {grouped.size === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-slate-500">No KPIs match your filters.</p>
        </div>
      ) : (
        Array.from(grouped.entries()).map(([category, kpis]) => (
          <div key={category}>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">{category}</h2>
            <div className="space-y-2">
              {kpis.map((kpi) => (
                <KPICard
                  key={kpi.metric}
                  kpi={kpi}
                  isExpanded={expandedIds.has(kpi.metric)}
                  onToggle={() => toggle(kpi.metric)}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

function SummaryCard({ label, value, icon: Icon, color }: {
  label: string;
  value: number;
  icon: React.ElementType;
  color?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${color || 'text-accent'}`} />
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}
